import { type FC, type KeyboardEvent, useRef, useState, useEffect, useCallback } from 'react';
import comboStyles from '../styles/combobox-form-field.module.scss';
import fieldStyles from '../styles/form-field.module.scss';

const ComboboxFormField: FC<{
    label: string;
    placeholder?: string;
    value: string;
    options: string[];
    onChange: (value: string) => void;
}> = ({ label, placeholder, value, options, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const containerRef = useRef<HTMLDivElement>(null);

    const filtered = value.trim()
        ? options.filter((o) => o.toLowerCase().includes(value.toLowerCase()))
        : options;

    const closeDropdown = useCallback(() => {
        setIsOpen(false);
        setHighlightedIndex(-1);
    }, []);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                closeDropdown();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [closeDropdown]);

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'Enter') {
                setIsOpen(true);
            }
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex((prev) => Math.min(prev + 1, filtered.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter') {
            if (highlightedIndex >= 0 && filtered[highlightedIndex] !== undefined) {
                onChange(filtered[highlightedIndex]);
            }
            closeDropdown();
        } else if (e.key === 'Escape') {
            closeDropdown();
        }
    };

    return (
        <div ref={containerRef} className={`${fieldStyles.formField} ${comboStyles.comboBox}`}>
            <div className={fieldStyles.fieldLabel}>
                <span>{label}</span>
            </div>
            <input
                type="text"
                className={fieldStyles.fieldInput}
                placeholder={placeholder}
                value={value}
                onChange={(e) => {
                    onChange(e.target.value);
                    setIsOpen(true);
                    setHighlightedIndex(-1);
                }}
                onFocus={() => setIsOpen(true)}
                onKeyDown={handleKeyDown}
            />
            {isOpen && filtered.length > 0 && (
                <div className={comboStyles.dropdown}>
                    {filtered.map((option, idx) => (
                        <div
                            key={option}
                            className={`${comboStyles.option}${idx === highlightedIndex ? ` ${comboStyles.highlighted}` : ''}`}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                onChange(option);
                                closeDropdown();
                            }}
                        >
                            {option}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ComboboxFormField;
